import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VariantContextData } from "../VariantBuilderContext";
import RemixSparkleIcon from "./RemixSparkleIcon";
import AttachmentUploader from "./AttachmentUploader";
import aiFocusBreadcrumb, { shortBreadcrumb } from "./aiFocusBreadcrumb";
import { setAiChatFocus } from "./useAiAssistant";
import BillableActionsMeter from "./BillableActionsMeter";
import { resolveElementCreationLabel } from "./elementCreationPrompt";
import {
  canUseAiRemixBuilderChat,
  remixAiUpgradePlan,
} from "../../../utils/canUseAiRemixText";
import {
  attachImportedAssetsFromEvent,
  attachmentFromClipboard,
  htmlOrMarkdownFilesFromDataTransfer,
  MAX_IMPORTED_ATTACHMENTS,
} from "../../../utils/aiPasteAttachment";
import {
  dataTransferHasFiles,
  dataTransferHasImages,
  UPLOAD_WAIT_MESSAGE,
  uploadFailureMessage,
} from "../../../utils/aiChatImageDrop";

const MAX_ATTACHMENTS = 5;

const OBJECT_LABELS = {
  Steps: "step",
  Sections: "section",
  Elements: "element",
};

const ARTICLES = {
  Steps: "a",
  Sections: "a",
  Elements: "an",
};

// Floating bar fixed to the bottom of the create panels (Steps / Sections /
// Elements), matching the Remix AI chat composer 1:1. Submitting a prompt
// (and any attached assets) opens the chat and asks it to create that kind
// of object. Rendered through a portal onto #options-panel so it overlays
// the panel without affecting its layout.
const RemixAiPromptBar = ({ objectType }) => {
  const [contextData, builder] = useContext(VariantContextData);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [dropError, setDropError] = useState("");
  const pendingUploadsRef = useRef(0);
  // remembered target key the user dismissed; a new selection produces a
  // different key, so the breadcrumb re-appears automatically
  const [dismissedTargetKey, setDismissedTargetKey] = useState(null);
  const textareaRef = useRef(null);
  const uploadsInFlight = useRef(0);

  // grow the composer with its content, up to ~6 lines, then scroll
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(
      Math.max(textarea.scrollHeight, 28),
      150
    )}px`;
  }, [draft]);

  const label = OBJECT_LABELS[objectType];
  const panelElement = document.getElementById("options-panel");
  const website = contextData.objects.website;
  const account = contextData.objects.account;
  const subscription = contextData.objects.subscription;
  const currentUser = contextData.objects.current_user;
  const aiUnlocked = canUseAiRemixBuilderChat({
    currentUser,
    website,
    subscription,
    account,
  });
  if (!label || !panelElement) return null;
  const new_object = contextData.new_object || {};

  const elementCreation =
    objectType === "Elements"
      ? resolveElementCreationLabel(new_object.element_type)
      : null;
  const creationLabel = elementCreation ? elementCreation.label : label;
  const creationArticle = elementCreation
    ? elementCreation.article
    : ARTICLES[objectType];

  // the parent the new object will be created inside: the create panel's
  // explicit target when present, otherwise the object that was selected
  // when the panel opened (captured in new_object, since opening the panel
  // clears the live selection)
  const resolveCreationTarget = () => {
    const variantObjects = contextData.objects.variant;
    const selectedType =
      contextData.selected_object_type || new_object.selection_object_type;
    const selectedId =
      contextData.selected_object_id || new_object.selection_object_id;
    const selectedElement =
      selectedType === "elements" && selectedId
        ? variantObjects.elements[selectedId]
        : null;

    if (objectType === "Sections") {
      if (new_object.step_id) {
        return { object_type: "steps", object_id: new_object.step_id };
      }
      if (selectedType === "steps" && selectedId) {
        return { object_type: "steps", object_id: selectedId };
      }
      if (selectedType === "sections" && selectedId) {
        const section = variantObjects.sections[selectedId];
        if (section && section.step_id) {
          return { object_type: "steps", object_id: section.step_id };
        }
      }
      if (selectedElement && selectedElement.section_id) {
        const section = variantObjects.sections[selectedElement.section_id];
        if (section && section.step_id) {
          return { object_type: "steps", object_id: section.step_id };
        }
      }
      return null;
    }

    if (objectType === "Elements") {
      if (new_object.section_id) {
        return { object_type: "sections", object_id: new_object.section_id };
      }
      if (
        ["sections", "section_columns"].includes(selectedType) &&
        selectedId
      ) {
        return { object_type: selectedType, object_id: selectedId };
      }
      if (selectedElement && selectedElement.section_id) {
        return {
          object_type: "sections",
          object_id: selectedElement.section_id,
        };
      }
      return null;
    }

    return null;
  };

  const creationTarget = resolveCreationTarget();
  const creationTargetKey = creationTarget
    ? `${creationTarget.object_type}:${creationTarget.object_id}`
    : null;
  const targetDismissed =
    dismissedTargetKey !== null && dismissedTargetKey === creationTargetKey;
  const targetBreadcrumb = targetDismissed
    ? null
    : aiFocusBreadcrumb(contextData, builder, creationTarget);

  const trackUpload = (delta) => {
    const next = Math.max(0, uploadsInFlight.current + delta);
    uploadsInFlight.current = next;
    pendingUploadsRef.current = next;
    setPendingUploads(next);
    if (next === 0) {
      setDropError((prev) => (prev === UPLOAD_WAIT_MESSAGE ? "" : prev));
    }
  };

  const addAttachment = (attachment) => {
    setAttachments((existing) => {
      if (existing.length >= MAX_ATTACHMENTS) {
        Promise.resolve().then(() =>
          setDropError(`You can attach up to ${MAX_ATTACHMENTS} attachments.`)
        );
        return existing;
      }
      return [...existing, attachment];
    });
  };

  const importAssetsFromEvent = (event) =>
    attachImportedAssetsFromEvent(event, {
      addAttachment,
      website,
      environment: contextData.environment,
      attachmentCount: attachments.length,
      maxAttachments: MAX_IMPORTED_ATTACHMENTS,
      pendingUploadsRef,
      onPendingUploadsChange: (count) => {
        const next = Number.isFinite(count) ? count : pendingUploadsRef.current;
        uploadsInFlight.current = next;
        pendingUploadsRef.current = next;
        setPendingUploads(next);
      },
      onLimitMessage: setDropError,
    });

  const handlePaste = (event) => {
    const transfer = event.clipboardData;
    const files = Array.from((transfer && transfer.files) || []);
    const hasImport =
      files.length > 0 || Boolean(attachmentFromClipboard(transfer));
    if (!hasImport) return;
    event.preventDefault();
    if (!aiUnlocked) {
      triggerRemixGate(event);
      return;
    }
    importAssetsFromEvent(event);
  };

  const handleComposerDragOver = (event) => {
    if (!dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect =
      dataTransferHasImages(event.dataTransfer) ||
      htmlOrMarkdownFilesFromDataTransfer(event.dataTransfer).length > 0
        ? "copy"
        : "none";
  };

  const handleComposerDrop = (event) => {
    if (!dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    if (!aiUnlocked) {
      triggerRemixGate(event);
      return;
    }
    importAssetsFromEvent(event);
  };

  const triggerRemixGate = (event) => {
    if (typeof subscriptions === "undefined" || !subscriptions.upgrade_modal) {
      return;
    }

    subscriptions.upgrade_modal(
      event || window.event,
      false,
      "build funnels with Remix AI",
      remixAiUpgradePlan({ subscription, website }),
      "Triggered feature CTA – Remix AI Builder Create"
    );
  };

  const submit = (event) => {
    const text = draft.trim();
    if ((!text && attachments.length === 0) || pendingUploads > 0) return;

    if (!aiUnlocked) {
      triggerRemixGate(event);
      return;
    }
    if (uploadsInFlight.current > 0) {
      setDropError(UPLOAD_WAIT_MESSAGE);
      return;
    }

    const variant = contextData.objects.variant;

    // carry the creation target into the chat as its focused object
    // (unless the user cleared the breadcrumb chip)
    if (creationTarget && !targetDismissed) {
      setAiChatFocus(
        variant.id,
        creationTarget.object_type,
        creationTarget.object_id
      );
    }

    const prompt = text
      ? `Create a new ${creationLabel}: ${text}`
      : `Create a new ${creationLabel} from the attached asset`;

    setDraft("");
    setAttachments([]);
    setDropError("");
    builder.view("ai");
    dispatchCustomEvent("remixAiPrompt", {
      prompt: prompt,
      attachments: attachments,
      element_type: elementCreation?.elementType,
    });
  };

  return createPortal(
    <div
      className="absolute bottom-0 left-0 right-0 z-30 px-4 pt-4 pb-0 bg-white dark:bg-slate-950 rounded-t-2xl rounded-b-xl border-0 border-t border-solid border-slate-100 dark:border-slate-800 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
      onDragOver={handleComposerDragOver}
      onDragEnter={handleComposerDragOver}
      onDrop={handleComposerDrop}
    >
      {dropError && <p className="mb-2 text-lg text-red-600">{dropError}</p>}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-2">
          {attachments.map((attachment, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-lg text-lg border border-solid border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white"
            >
              <FontAwesomeIcon icon="fas fa-paperclip" className="mr-2" />
              {attachment.name}
              <button
                className="ml-2 bg-transparent border-0 cursor-pointer text-slate-500 p-0"
                onClick={() => {
                  setDropError("");
                  setAttachments((existing) =>
                    existing.filter((_, i) => i !== index)
                  );
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2">
        <span className="shrink-0">
          <RemixSparkleIcon width={12} height={13} />
        </span>
        <textarea
          ref={textareaRef}
          className="flex-1 min-w-0 min-h-[28px] max-h-[150px] text-xl p-0 bg-transparent text-gray-900 dark:text-white resize-none overflow-y-auto overflow-x-hidden border-none outline-none focus:border-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 shadow-none focus:shadow-none placeholder-gray-500 leading-relaxed"
          style={{ border: 0, outline: "none", boxShadow: "none" }}
          rows={1}
          placeholder={`Create ${creationArticle} ${creationLabel} with AI instructions`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPaste={handlePaste}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit(event);
            }
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-2 mb-2">
        <div className="relative flex items-center gap-2 min-w-0">
          <AttachmentUploader
            onAttach={addAttachment}
            onUploadBusy={trackUpload}
            onUploadError={(reason) =>
              setDropError(uploadFailureMessage(new Error(reason)))
            }
          />
          {elementCreation && (
            <div
              className="h-[30px] shrink-0 flex items-center gap-2 px-3 rounded-full border border-solid border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white text-xl capitalize"
              data-testid="remix-ai-prompt-element-type"
              title={`Creating a ${elementCreation.label}`}
            >
              {elementCreation.label}
            </div>
          )}
          {targetBreadcrumb && (
            <div
              className="h-[30px] min-w-0 flex items-center gap-2 px-3 rounded-full border border-solid border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white text-xl"
              data-testid="remix-ai-prompt-target"
            >
              <span
                className="min-w-0 flex items-center gap-2"
                title={`Creating inside ${targetBreadcrumb}`}
              >
                <FontAwesomeIcon icon="fas fa-arrow-pointer" />
                <span className="truncate whitespace-nowrap">
                  {shortBreadcrumb(targetBreadcrumb)}
                </span>
              </span>
              <button
                className="shrink-0 p-0 bg-transparent border-0 cursor-pointer text-slate-500 hover:text-black dark:hover:text-white"
                title="Remove target"
                data-testid="remix-ai-prompt-target-clear"
                onClick={() => setDismissedTargetKey(creationTargetKey)}
              >
                ×
              </button>
            </div>
          )}
        </div>
        <button
          className={`w-[30px] h-[30px] shrink-0 ml-2 flex items-center justify-center rounded-full border-0 bg-blue-600 text-white transition-colors p-0 ${
            pendingUploads === 0 && (draft.trim() || attachments.length > 0)
              ? "cursor-pointer hover:bg-blue-700"
              : "opacity-50 cursor-not-allowed"
          }`}
          style={{ minWidth: "30px", minHeight: "30px" }}
          title="Create with Remix AI"
          disabled={
            pendingUploads > 0 || (!draft.trim() && attachments.length === 0)
          }
          onClick={(event) => submit(event)}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 3V15M9 3L4 8M9 3L14 8"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="-mx-4">
        <BillableActionsMeter
          usage={account && account.billable_actions_usage}
          limit={account && account.billable_actions_limit}
          accountId={account && account.id}
          websiteId={website && website.id}
        />
      </div>
    </div>,
    panelElement
  );
};

export default RemixAiPromptBar;
